import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSession } from "@/features/auth/hooks/useSession";
import { getActiveWorkspaceId } from "@/lib/api/workspace-context";
import { useEscapeKey } from "@/common/hooks/useEscapeKey";
import {
  getInterviewQuestions,
  submitInterviewAnswer,
  type InterviewQuestion,
} from "@/lib/api/endpoints/profile-interview";
import "./InterviewPanel.css";

type Bubble =
  | { role: "xavior"; text: string }
  | { role: "user"; text: string }
  | { role: "noted"; items: string[] };

const GREETING =
  "I'll ask a few quick questions so I actually know you — your people, your world, how you run. Answer however you like; I'll note what matters as we go.";

/**
 * Profile interview — Xavior asks, you answer, and each answer is extracted into
 * your profile (dimensions + contacts) so Synthesis stops being blank. Rendered
 * as a chat thread. On close, calls onComplete so the caller can regenerate.
 */
// Dynamic openers so the discussion never reads identically twice.
const SEEDED_GREETINGS = [
  "Go ahead — tell me what you'd like me to know.",
  "I'm listening — what's on your mind?",
  "Sure, let's get into it. Where would you like to start?",
  "Happy to. Tell me whatever comes to mind.",
  "Okay — fire away.",
];
const REMINDERS = [
  "The more you tell me, the more useful I become.",
  "Every detail you share makes me sharper about your world.",
  "The more I know, the more I can help — keep it coming.",
  "Small details add up — the more you share, the better I get.",
];
// Continuous-mode follow-ups: after each answer, invite more rather than ending.
const FOLLOWUPS = [
  "Got it. What else? More milestones, places you've lived, or work.",
  "Noted. Anything else to add — other roles, moves, or life events?",
  "Added. Keep going if there's more, or hit Done when you're set.",
  "Got that. What else should I know?",
];
const pickOne = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];

export function InterviewPanel({
  onClose,
  onComplete,
  seedQuestion,
  title = "Tell Xavior about you",
  onAnswer = submitInterviewAnswer,
  continuous = false,
}: {
  onClose: () => void;
  onComplete?: () => void;
  /** When set (e.g. launched from a Synthesis "Needs you" item), the interview
   *  opens by asking about this specific gap before the general script. */
  seedQuestion?: string;
  /** Header label (so Work can say "Talk to Xavior about work"). */
  title?: string;
  /** Submit handler — defaults to the profile interview; Work overrides it. */
  onAnswer?: typeof submitInterviewAnswer;
  /** Open-ended mode: after each answer, re-prompt for more instead of ending,
   *  so the user can keep adding (e.g. History — events, places, work). The
   *  input stays open; the user finishes via Done / ✕. */
  continuous?: boolean;
}) {
  const [greeting] = useState(() => pickOne(SEEDED_GREETINGS));
  const [reminder] = useState(() => pickOne(REMINDERS));
  const { token } = useSession();
  const ws = getActiveWorkspaceId();
  const [questions, setQuestions] = useState<InterviewQuestion[]>([]);
  const [idx, setIdx] = useState(0);
  const [thread, setThread] = useState<Bubble[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [capturedAnything, setCapturedAnything] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Closing ALWAYS regenerates Synthesis if anything was captured — whether via
  // the Done button, the ✕, Escape, or clicking the backdrop.
  const close = () => {
    if (capturedAnything) onComplete?.();
    onClose();
  };

  useEscapeKey(close, true);

  // Load the question script and open with the greeting + first question.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { questions: qs, filledDimensions } = await getInterviewQuestions(token ?? null, ws);
        if (!alive) return;
        // Gap-driven: drop questions whose dimensions are ALL already filled, so
        // Xavior never re-asks what it knows. A seed question (from a Synthesis
        // gap) is always asked first.
        const filled = new Set(filledDimensions ?? []);
        const gaps = qs.filter(
          (q) => !(q.dimensions?.length && q.dimensions.every((d) => filled.has(d))),
        );
        // A seeded interview (from a tab's "tell me more" CTA or a gap) asks ONLY
        // the seed — never the overlapping script question, which read as a repeat.
        const seeded = seedQuestion
          ? [{ id: "seed", prompt: seedQuestion, hint: "" }]
          : gaps;
        setQuestions(seeded);
        setThread([
          {
            role: "xavior",
            text: seedQuestion
              ? greeting
              : filled.size > 0
                ? "I already know a bit about you — let me just fill the gaps."
                : GREETING,
          },
          ...(seeded[0]
            ? [{ role: "xavior" as const, text: seeded[0].prompt }]
            : [{ role: "xavior" as const, text: "Actually, I've got a solid picture already — nothing missing right now. You can always add more later." }]),
        ]);
        if (!seeded[0]) setDone(true);
      } catch {
        if (alive) setThread([{ role: "xavior", text: "I couldn't start the interview just now. Try again in a moment." }]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, ws, seedQuestion]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [thread, busy]);

  const current = questions[idx];

  const submit = async () => {
    const answer = input.trim();
    if (!answer || busy || !current) return;
    setInput("");
    setThread((t) => [...t, { role: "user", text: answer }]);
    setBusy(true);
    let failed = false;
    try {
      const res = await onAnswer(token ?? null, ws, current.prompt, answer);
      // Extraction FAILED on the server (LLM/proxy down or unparseable output) —
      // distinct from "extracted nothing." Surface a retryable error and DON'T
      // advance, so the user can resend instead of silently losing the turn
      // (#4371 — previously this was an invisible 200-but-empty no-op).
      if (res.extractionFailed) {
        failed = true;
        setThread((t) => [...t, { role: "noted", items: ["Couldn't save that just now — try again."] }]);
      } else {
        const noted = res.noted?.length
          ? res.noted
          : res.contactsCreated?.length || res.dimensionsUpdated?.length || res.contactsUpdated?.length
            ? [`Noted ${[...res.contactsCreated, ...res.dimensionsUpdated, ...(res.contactsUpdated ?? [])].join(", ")}`]
            : [];
        if (noted.length) {
          setCapturedAnything(true);
          setThread((t) => [...t, { role: "noted", items: noted }]);
        }
      }
    } catch {
      failed = true;
      setThread((t) => [...t, { role: "noted", items: ["Couldn't save that just now — try again."] }]);
    } finally {
      setBusy(false);
    }
    // Continuous mode: never finish on our own — re-prompt and keep the same
    // question active so the input stays open for the next addition. This runs
    // even after a failure: the question never advances in continuous mode, so
    // we still invite the next turn rather than leaving the thread silent under
    // the retry line (#4371 — the failure path must not swallow the follow-up).
    if (continuous) {
      setThread((t) => [...t, { role: "xavior", text: pickOne(FOLLOWUPS) }]);
      return;
    }
    // Step-by-step mode: on failure keep the SAME question active so the user
    // can resend — never advance or finish on a turn that didn't land.
    if (failed) return;
    // Advance to the next question (or finish).
    const next = idx + 1;
    setIdx(next);
    if (questions[next]) {
      setThread((t) => [...t, { role: "xavior", text: questions[next].prompt }]);
    } else {
      setThread((t) => [...t, { role: "xavior", text: "That's a great start — I've got enough to fill in your picture. Regenerate Synthesis and you'll see it." }]);
      setDone(true);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return createPortal(
    <div className="interview-overlay" onMouseDown={close}>
      <div className="interview-panel" onMouseDown={(e) => e.stopPropagation()}>
        <div className="interview-header">
          <div className="interview-title">{title}</div>
          <button className="interview-close" onClick={close} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="interview-thread xv-scroll" ref={scrollRef}>
          {thread.map((b, i) =>
            b.role === "noted" ? (
              <div key={i} className="interview-noted">
                {b.items.map((it, j) => (
                  <div key={j} className="interview-noted-line">
                    <span className="interview-noted-check">✓</span> {it}
                  </div>
                ))}
              </div>
            ) : (
              <div key={i} className={`interview-bubble interview-bubble-${b.role}`}>
                {b.text}
              </div>
            ),
          )}
          {busy && <div className="interview-bubble interview-bubble-xavior interview-typing">…</div>}
        </div>

        <div className="interview-reminder">💡 {reminder}</div>

        <div className="interview-input-row">
          {done ? (
            <button className="interview-done" onClick={close}>
              Done — update Synthesis
            </button>
          ) : (
            <>
              <textarea
                className="interview-input"
                placeholder={current ? "Type your answer…" : "Starting…"}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={busy || !current}
                rows={2}
                autoFocus
              />
              <button className="interview-send" onClick={submit} disabled={busy || !input.trim()}>
                Send
              </button>
              {continuous && (
                <button className="interview-done-inline" onClick={close} disabled={busy}>
                  Done
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default InterviewPanel;
